/**
 * Конструктор отчётов: срезы по трём измерениям.
 *
 * Устройство взято у iiko — поля делятся на группировки и данные —
 * но с двумя нашими правилами: период ограничен жёстко (год),
 * а не предупреждением, и малая выборка честно называется малой.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DIMENSION_LABEL, MEASURE_LABEL,
  type Dimension, type Measure, type ReportResult, type ReportTemplate,
} from '@dastarhan/contracts';

const API = '/api/v1/report-builder';

const DIMS: Dimension[] = [
  'day', 'week', 'month', 'weekday', 'hour',
  'location', 'category', 'product', 'cashier', 'waiter',
  'payMethod', 'orderMode', 'table',
];
const MEASURES: Measure[] = [
  'revenue', 'checks', 'avgCheck', 'qty', 'discount',
  'cost', 'margin', 'marginPct', 'share',
];

/** Деньги в мерах — тиыны; проценты и штуки — как есть. */
const MONEY: Measure[] = ['revenue', 'avgCheck', 'discount', 'cost', 'margin'];
const PCT: Measure[] = ['marginPct', 'share'];

const fmt = (m: Measure, v: number) =>
  MONEY.includes(m) ? `${Math.round(v / 100).toLocaleString('ru-RU')} ₸`
    : PCT.includes(m) ? `${v}%`
      : v.toLocaleString('ru-RU');

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86400_000).toISOString().slice(0, 10);

async function call<T>(path: string, token: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    method: opts.method ?? 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t.includes('PERIOD_TOO_LONG')
      ? 'Период больше года — сузьте рамки, иначе отчёт будет считаться слишком долго'
      : 'Не удалось построить отчёт');
  }
  return res.json() as Promise<T>;
}

export function ReportBuilderScreen({ token }: { token: string }) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [dims, setDims] = useState<Dimension[]>(['day']);
  const [meas, setMeas] = useState<Measure[]>(['revenue', 'checks', 'avgCheck']);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [tplName, setTplName] = useState('');
  const [shared, setShared] = useState(false);

  const templates = useQuery({
    queryKey: ['rb-templates'],
    queryFn: () => call<ReportTemplate[]>('/templates', token),
  });

  const build = useMutation({
    mutationFn: () => call<ReportResult>('/build', token, {
      method: 'POST',
      body: {
        from, to: new Date(new Date(to).getTime() + 86400_000).toISOString().slice(0, 10),
        dimensions: dims, measures: meas, limit: 200,
      },
    }),
    onSuccess: (r) => { setResult(r); setErr(''); },
    onError: (e: Error) => { setErr(e.message); setResult(null); },
  });

  const saveTpl = useMutation({
    mutationFn: () => call('/templates', token, {
      method: 'POST',
      body: { name: tplName, dimensions: dims, measures: meas, shared },
    }),
    onSuccess: () => {
      setSaving(false); setTplName('');
      void qc.invalidateQueries({ queryKey: ['rb-templates'] });
    },
  });

  const delTpl = useMutation({
    mutationFn: (id: string) => call(`/templates/${id}`, token, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rb-templates'] }),
  });

  const toggleDim = (d: Dimension) => {
    setDims((cur) => cur.includes(d)
      ? cur.filter((x) => x !== d)
      : cur.length >= 3 ? cur : [...cur, d]);
  };
  const toggleMeas = (m: Measure) => {
    setMeas((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);
  };

  /** Выгрузка с параметрами построения в шапке — чтобы файл сам себя объяснял. */
  const exportCsv = () => {
    if (!result) return;
    const head = [
      `Отчёт: ${dims.map((d) => DIMENSION_LABEL[d]).join(' × ')}`,
      `Период: ${from} — ${to}`,
      `Построен: ${new Date().toLocaleString('ru-RU')}`,
      '',
    ].join('\n');
    const cols = [...dims.map((d) => DIMENSION_LABEL[d]), ...meas.map((m) => MEASURE_LABEL[m])];
    const lines = result.rows.map((r) => [
      ...r.keys,
      ...meas.map((m) => (MONEY.includes(m) ? (r.values[m] ?? 0) / 100 : r.values[m] ?? 0)),
    ].join(';'));
    const csv = `${head}${cols.join(';')}\n${lines.join('\n')}`;
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `отчёт-${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const periodDays = useMemo(
    () => Math.ceil((new Date(to).getTime() - new Date(from).getTime()) / 86400_000) + 1,
    [from, to],
  );

  return (
    <section className="menu-cat rb">
      <header>
        <h2>Конструктор отчётов</h2>
        <div className="rb-period">
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          <span>—</span>
          <input type="date" value={to} min={from} max={today()} onChange={(e) => setTo(e.target.value)} />
          <span className="hint">{periodDays} дн.</span>
          {([[7, 'неделя'], [30, 'месяц'], [90, 'квартал']] as const).map(([d, label]) => (
            <button key={d} className="chip" onClick={() => { setFrom(daysAgo(d)); setTo(today()); }}>
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="rb-fields">
        <div className="rb-col">
          <h3>Разрез <span className="hint">до трёх</span></h3>
          <div className="rb-chips">
            {DIMS.map((d) => (
              <button key={d}
                className={`chip ${dims.includes(d) ? 'on' : ''}`}
                disabled={!dims.includes(d) && dims.length >= 3}
                onClick={() => toggleDim(d)}>
                {DIMENSION_LABEL[d]}
              </button>
            ))}
          </div>
        </div>

        <div className="rb-col">
          <h3>Показатели</h3>
          <div className="rb-chips">
            {MEASURES.map((m) => (
              <button key={m}
                className={`chip ${meas.includes(m) ? 'on' : ''}`}
                onClick={() => toggleMeas(m)}>
                {MEASURE_LABEL[m]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="rb-actions">
        <button className="btn primary" disabled={build.isPending || dims.length === 0 || meas.length === 0}
          onClick={() => build.mutate()}>
          {build.isPending ? 'Считаем…' : 'Построить'}
        </button>
        {result && <button className="btn" onClick={exportCsv}>Выгрузить CSV</button>}
        {result && <button className="btn" onClick={() => setSaving(true)}>Сохранить как шаблон</button>}
        {result && <span className="hint">посчитано за {result.ms} мс</span>}
      </div>

      {err && <p className="err">{err}</p>}

      {(templates.data?.length ?? 0) > 0 && (
        <div className="rb-templates">
          <h3>Шаблоны</h3>
          {templates.data!.map((t) => (
            <span key={t.id} className="tpl">
              <button className="chip" onClick={() => {
                setDims(t.dimensions); setMeas(t.measures);
              }}>{t.name}</button>
              {!t.shared && <i className="hint">личный</i>}
              {t.mine && (
                <button className="tpl-x" title="Удалить шаблон"
                  onClick={() => delTpl.mutate(t.id)}>×</button>
              )}
            </span>
          ))}
        </div>
      )}

      {result && (
        <>
          {result.warning && <p className="rb-warn">{result.warning}</p>}
          {result.truncated > 0 && (
            <p className="hint">
              Показаны первые {result.rows.length} строк, ещё {result.truncated} не поместились —
              сузьте период или уберите одно измерение.
            </p>
          )}

          {result.rows.length > 0 && (
            <table className="grid rb-table">
              <thead>
                <tr>
                  {result.dimensions.map((d) => <th key={d}>{DIMENSION_LABEL[d]}</th>)}
                  {result.measures.map((m) => <th key={m} className="num">{MEASURE_LABEL[m]}</th>)}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i}>
                    {r.keys.map((k, j) => <td key={j}>{k}</td>)}
                    {result.measures.map((m) => (
                      <td key={m} className="num">{fmt(m, r.values[m] ?? 0)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={result.dimensions.length}><b>Итого</b></td>
                  {result.measures.map((m) => (
                    <td key={m} className="num"><b>{fmt(m, result.totals[m] ?? 0)}</b></td>
                  ))}
                </tr>
              </tfoot>
            </table>
          )}
        </>
      )}

      {saving && (
        <div className="dialog">
          <div className="dialog-card">
            <h2>Сохранить шаблон</h2>
            <p className="hint">
              Назовите так, чтобы имя отвечало на вопрос «что я увижу»:
              «Выручка по дням» лучше, чем «Отчёт 1».
            </p>
            <input value={tplName} placeholder="Например: Блюда по категориям за месяц"
              onChange={(e) => setTplName(e.target.value)} />
            <label className="check">
              <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
              Виден всем сотрудникам с доступом в офис
            </label>
            <div className="modal-actions">
              <button className="btn" onClick={() => setSaving(false)}>Отмена</button>
              <button className="btn primary" disabled={tplName.trim().length < 2}
                onClick={() => saveTpl.mutate()}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
